import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { IDragEvent, TDragImageOffsetFunction, TDropEffect, TEffectAllowed } from '@df/ui/drag/drag.interface';
import { filterEffects } from '@df/ui/drag/utils';

@Injectable({ providedIn: 'root' })
export class DragService {
    static MIME_TYPE = 'custom-data';
    static MSIE_MIME_TYPE = 'Text';
    static DROP_EFFECTS = ['move', 'copy', 'link'] as TDropEffect[];

    startDropzoneId?: string;
    endDropzoneId?: string;
    lastIndex?: number;

    isDragging = false;
    dropEffect ?: string = 'none';
    effectAllowed ?: TEffectAllowed = 'all';
    type?: string = undefined;

    dragData: any;
    scope?: string | Array<string>;
    onDragStart = new Subject<void>();
    onDragEnd = new Subject<void>();
    disabled$ = new Subject<boolean>();

    /** drag service state */
    disabled = false;

    startDrag(event: IDragEvent, data: any, effectAllowed: TEffectAllowed, type: string | undefined) {
        this.isDragging = true;
        this.dropEffect = 'none';
        this.effectAllowed = effectAllowed;
        this.type = type;
        this.dragData = data;

        event.dataTransfer!.effectAllowed = effectAllowed;
    }

    endDrag() {
        this.isDragging = false;
        this.dropEffect = undefined;
        this.effectAllowed = undefined;
        this.type = undefined;
        this.dragData = null;
    }

    setDragImage(event: DragEvent, dragImage: Element, offsetFunction: TDragImageOffsetFunction): void {
        const offset = offsetFunction(event, dragImage) || { x: 0, y: 0 };

        (event.dataTransfer as any).setDragImage(dragImage, offset.x, offset.y);
    }

    getDragType(event: IDragEvent): string | undefined {
        if (this.isDragging === true) {
            return this.type;
        }

        const mimeType = this.getWellKnownMimeType(event);

        if (mimeType === null) {
            return undefined;
        }

        if (mimeType === DragService.MSIE_MIME_TYPE || mimeType === DragService.MIME_TYPE) {
            return undefined;
        }

        return undefined;
    }

    setDropEffect(event: IDragEvent, dropEffect: TDropEffect) {
        if (this.isDragging === true) {
            this.dropEffect = dropEffect;
        }

        event.dataTransfer!.dropEffect = dropEffect;
    }

    getDropEffect(event: IDragEvent, effectAllowed?: TEffectAllowed | TDropEffect): TDropEffect {
        const dataTransferEffectAllowed: TEffectAllowed = event.dataTransfer
            ? (event.dataTransfer.effectAllowed as TEffectAllowed)
            : 'uninitialized';

        let effects = filterEffects(DragService.DROP_EFFECTS, dataTransferEffectAllowed);

        if (this.isDragging === true) {
            effects = filterEffects(effects, this.effectAllowed!);
        }

        if (effectAllowed) {
            effects = filterEffects(effects, effectAllowed);
        }

        // MacOS automatically filters dataTransfer.effectAllowed depending on the modifier keys,
        // therefore the following modifier keys will only affect other operating systems.
        if (effects.length === 0) {
            return 'none';
        }

        if (event.ctrlKey && effects.indexOf('copy') !== -1) {
            return 'copy';
        }

        if (event.altKey && effects.indexOf('link') !== -1) {
            return 'link';
        }

        return effects[0] as TDropEffect;
    }

    protected getWellKnownMimeType(event: DragEvent): string | null {
        if (event.dataTransfer) {
            const types = event.dataTransfer.types;

            // IE 9 workaround.
            if (!types) {
                return DragService.MSIE_MIME_TYPE;
            }

            // tslint:disable-next-line: prefer-for-of
            for (let i = 0; i < types.length; i++) {
                if (types[i] === DragService.MSIE_MIME_TYPE || types[i] === DragService.MIME_TYPE) {
                    return types[i];
                }
            }
        }

        return null;
    }

    isExternalDrag(): boolean {
        return this.isDragging === false;
    }
}
